import { useCallback } from 'react';

import { TabsContent } from '@/components/ui/tabs';

import {
  type ContextSelectionItem,
  ContextSelectionType,
  useContextSelection,
} from '@/components/chat/ContextSelectionContext';
import { ChatContextMenuItem } from '@/components/chat/chat-context-menu/ChatContextMenuItem';
import { ChatContextMenuItemSkeleton } from '@/components/chat/chat-context-menu/ChatContextMenuItemSkeleton';
import { ExperimentItem } from '@/components/chat/chat-context-menu/hooks/useExperiments';
import { ProductItem } from '@/components/chat/chat-context-menu/hooks/useProducts';
import { ToolItem } from '@/components/chat/chat-context-menu/hooks/useTools';
import { ToolVersionSelector } from '@/components/chat/chat-context-menu/tools/ToolVersionSelector';
import { useToolVersionPickerFlow } from '@/components/chat/chat-context-menu/useToolVersionPickerFlow';

interface ChatContextMenuTabProps {
  type: ContextSelectionType;
  loading: boolean;
  items: ExperimentItem[] | ProductItem[] | ToolItem[];
}

const runChatContextMenuTabItemAction = (
  item: ExperimentItem | ProductItem | ToolItem,
  tabType: ContextSelectionType,
  handleCommit: (selection: ContextSelectionItem) => void,
  requestToolSelection: (
    toolItem: ToolItem,
    selectionType: ContextSelectionType
  ) => void
) => {
  if (tabType !== ContextSelectionType.Tool) {
    const selection: ContextSelectionItem = {
      id: item.id,
      title: item.name,
      type: tabType,
      lastEditLabel: item.updatedAt ?? '',
    };

    handleCommit(selection);

    return;
  }

  requestToolSelection(item as ToolItem, tabType);
};

interface ChatContextMenuTabViewProps {
  type: ContextSelectionType;
  loading: boolean;
  items: ExperimentItem[] | ProductItem[] | ToolItem[];
  handleClick: (item: ExperimentItem | ProductItem | ToolItem) => void;
  pendingToolItem: ToolItem | null;
  isSheetOpen: boolean;
  confirmVersionSelection: (selectedIds: string[]) => void;
  handleSheetOpenChange: (open: boolean) => void;
  closeSheet: () => void;
}

const ChatContextMenuTabView = ({
  type,
  loading,
  items,
  handleClick,
  pendingToolItem,
  isSheetOpen,
  confirmVersionSelection,
  handleSheetOpenChange,
  closeSheet,
}: ChatContextMenuTabViewProps) => (
  <>
    <TabsContent value={type} className="mt-2 max-h-[320px] overflow-y-auto">
      {loading &&
        [1, 2].map((item) => <ChatContextMenuItemSkeleton key={item} />)}
      {items.map((item: ExperimentItem | ProductItem | ToolItem) => (
        <ChatContextMenuItem key={item.id} onClick={() => handleClick(item)}>
          {item.name}
        </ChatContextMenuItem>
      ))}
    </TabsContent>
    {pendingToolItem && (
      <ToolVersionSelector
        open={isSheetOpen}
        onOpenChange={handleSheetOpenChange}
        onClose={closeSheet}
        onConfirm={confirmVersionSelection}
        versions={pendingToolItem.versions.map((version) => ({
          id: version.id,
          name: version.name,
          editedAt: version.editedAt ?? '',
        }))}
        toolName={pendingToolItem.name}
      />
    )}
  </>
);

export const ChatContextMenuTab = ({
  type,
  loading,
  items,
}: ChatContextMenuTabProps) => {
  const { addContextSelection, runOnSelectItem } = useContextSelection();

  const handleCommit = useCallback(
    (selection: ContextSelectionItem) => {
      addContextSelection(selection);
      runOnSelectItem(selection);
    },
    [addContextSelection, runOnSelectItem]
  );

  const {
    pendingToolItem,
    isSheetOpen,
    requestToolSelection,
    confirmVersionSelection,
    handleSheetOpenChange,
    closeSheet,
  } = useToolVersionPickerFlow({ onCommit: handleCommit });

  const handleClick = useCallback(
    (item: ExperimentItem | ProductItem | ToolItem) => {
      runChatContextMenuTabItemAction(
        item,
        type,
        handleCommit,
        requestToolSelection
      );
    },
    [type, handleCommit, requestToolSelection]
  );

  return (
    <ChatContextMenuTabView
      type={type}
      loading={loading}
      items={items}
      handleClick={handleClick}
      pendingToolItem={pendingToolItem}
      isSheetOpen={isSheetOpen}
      confirmVersionSelection={confirmVersionSelection}
      handleSheetOpenChange={handleSheetOpenChange}
      closeSheet={closeSheet}
    />
  );
};
